import { AbilityBuilder } from '@casl/ability';
import type { Client, StompSubscription } from '@stomp/stompjs';
import produce from 'immer';
import { HelproomShiftPermissions, ShiftNames } from 'packages/workspace/modules/algebra/constants';

import { ActiveHelproomTaskComponentType, HelproomMessagingTopics, WebSocketTypes } from 'packages/workspace/modules/algebra/constants/helproom';
import useObservable from 'packages/workspace/modules/helpers/hooks/useObservable';
import { BehaviorSubject } from 'rxjs';
import { EventFormat, EventTypes } from '../../models/global-models';
import type { IHelpRoomOperator } from '../../models/help-room-operator';
import type {
  IHelpoomAccess,
  IHelproomActiveTask,
  IHelproomShiftOperator,
  IHelproomWithEverything,
  IMessage,
  IParticipant,
  IStaffersConversationGroup,
  IStafferVisitorOneOnOne,
  IVisitor,
} from '../../models/helprooms';
import { AppAbilities } from '../../../modules/helpers/contexts/abilities';
import { AbilityStore } from '../abilities-store';
import { SoundOptions } from '../useSoundStore';

/**
 *Utilities
 */
const addIsRead = (message: IMessage, is_read: boolean) => {
  return produce(message, (draftMessage) => {
    draftMessage.is_read = is_read;
  });
};

/**
 **Main Interface
 */
export interface IHelproomShift {
  started: boolean;
  active_subscriptions: StompSubscription[];
  helprooms: Array<IHelproomWithEverything>;
  helprooms_staffs_chats: Array<IStaffersConversationGroup>;
  visitors_one_on_one: Array<IStafferVisitorOneOnOne>;
  operator: IHelproomShiftOperator;
  client: Client;
  active_component: IHelproomActiveTask;
  messages_for_action: Record<string, IMessage[]>;
  messages: Record<string, IMessage[]>;
  messages_staffs: Record<string, IMessage[]>;
  messages_one_on_one: Record<string, IMessage[]>;
  access: IHelpoomAccess;
  pongUUID?: string;
}
/**
 ** Initial State
 */
export const initialState: IHelproomShift = {
  started: false,
  active_subscriptions: [],
  helprooms: [],
  helprooms_staffs_chats: [],
  visitors_one_on_one: [],
  operator: null as unknown as IHelproomShiftOperator,
  client: null as unknown as Client,
  active_component: null as unknown as IHelproomActiveTask,
  messages_for_action: {},
  messages: {},
  messages_staffs: {},
  messages_one_on_one: {},
  access: {
    approve_message: false,
    clear_messages: false,
    close_helproom_door: false,
    edit_message: false,
    chat: false,
    kick_out: false,
    one_on_one_invitation: false,
    reject_message: false,
    session_update: false,
  },
  pongUUID: ''
};

const helproomsShift$ = new BehaviorSubject(initialState);

export const HelproomsShiftStore = {
  /**
   * Called When We Begin Shift.
   * @param shiftHelprooms
   */
  beginShift: (shiftHelprooms: Array<IHelproomWithEverything>, operator: IHelproomShiftOperator, access: IHelpoomAccess): void => {
    const updatedHelprooms = shiftHelprooms.map((eachHelproom) => {
      const transformed_operators = Object.entries(eachHelproom.operators).reduce((modifiedOperators: Record<string, Partial<IHelpRoomOperator>>, [_, op]) => {
        if (op.id) {
          modifiedOperators[op.id] = {
            id: op.id,
            display_name: op.account_name,
            is_moderator: true,
          };
        }
        return modifiedOperators;
      }, {});
      const updated_visitors = Object.entries(eachHelproom.visitors).reduce((modifiedVisitors: Record<string, IVisitor>, [_, visitor]) => {
        modifiedVisitors[visitor.id] = produce(visitor, (draftVisitor) => {
          draftVisitor.one_on_one_initiated = visitor.one_on_one_conversations?.[operator.id] !== undefined;
        });
        return modifiedVisitors;
      }, {});
      return produce(eachHelproom, (draftHelproom) => {
        draftHelproom.helproom_id = eachHelproom.id;
        draftHelproom.visitors = {
          ...updated_visitors,
          ...transformed_operators,
        };
      });
    });
    const helprooms_staffs_chats = shiftHelprooms.map((eachHelproom) => {
      return {
        id: eachHelproom.id,
        helproom: eachHelproom,
        helproom_id: eachHelproom.id,
        display_name: `${eachHelproom.helproom.display_name}- Staffs`,
      };
    });
    const visitors_one_on_one = Object.entries(operator.one_on_one_conversations).map(([visitor_id, eachVisitor]) => {
      return produce(eachVisitor, (draftVisitor) => {
        draftVisitor.id = visitor_id;
        draftVisitor.visitor_id = visitor_id;
        draftVisitor.display_name = `${eachVisitor.visitor}-${eachVisitor.operator}`;
      });
    });
    //access related code
    const rules = defineHelproomAbilitiesForOperator(access);
    AbilityStore.updateRules(rules);

    const state = produce(helproomsShift$.value, (draftState) => {
      draftState.helprooms = updatedHelprooms;
      draftState.started = true;
      draftState.operator = operator;
      draftState.access = access;
      draftState.helprooms_staffs_chats = helprooms_staffs_chats;
      draftState.visitors_one_on_one = visitors_one_on_one;
    });
    helproomsShift$.next(state);
  },
  /**
   * Used when ending shift.
   */
  endShift: (): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      draftState.active_subscriptions.forEach((eachSubs$: StompSubscription) => eachSubs$.unsubscribe());
      draftState.client?.deactivate();
      return initialState;
    });
    const rules = revokeRulesForOperator();
    AbilityStore.updateRules(rules);
    helproomsShift$.next(state);
  },
  /**
   * Set Client
   */
  setClient: (client: Client): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      draftState.client = client;
    });
    helproomsShift$.next(state);
  },
  /**
   *Update One on One Stomp Subscriptions for Visitors
   *!NOTE: Not needed for helprooms/helproom staffs.
   */
  updateOneOnOneStompSubsription: (subscriptions$: StompSubscription, visitor_id: string, helproom_id: string): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      draftState.helprooms.forEach((helproom) => {
        if (helproom.id === helproom_id) {
          helproom.visitors[visitor_id].one_on_one_initiated = true;
          if (subscriptions$) {
            helproom.visitors[visitor_id].subscription = subscriptions$;
          }
        }
      });
    });
    helproomsShift$.next(state);
  },
  /**
   * Set Active Helproom Component
   * @param component
   * @param type
   */
  setActiveHelproomTaskComponent: (
    component: IHelproomWithEverything | IStaffersConversationGroup | IStafferVisitorOneOnOne,
    type: ActiveHelproomTaskComponentType.helproomActiveSession | ActiveHelproomTaskComponentType.staffersGroup | ActiveHelproomTaskComponentType.visitorOneOnOne
  ): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      if (type === ActiveHelproomTaskComponentType.helproomActiveSession) {
        draftState.messages?.[component.helproom_id]?.forEach((eachMessage) => addIsRead(eachMessage, true));
      } else if (type === ActiveHelproomTaskComponentType.staffersGroup) {
        draftState.messages_staffs?.[component.helproom_id]?.forEach((eachMessage) => addIsRead(eachMessage, true));
      } else if (type === ActiveHelproomTaskComponentType.visitorOneOnOne) {
        draftState.messages_one_on_one?.[component.id]?.forEach((eachOneonOne) => (eachOneonOne.is_read = true));
      }
      draftState.active_component = {
        type,
        component,
      };
    });
    helproomsShift$.next(state);
  },
  /**
   * Active messages from visitor gets pushed here.
   * @param message
   * @param helproom_id
   */
  pushToMessagesForAction: (message: EventFormat<IMessage>, helproom_id: string): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      if (!draftState.messages_for_action[helproom_id]) {
        draftState.messages_for_action[helproom_id] = [];
      }
      draftState.messages_for_action[helproom_id] = produce(
        draftState.messages_for_action[helproom_id].filter((eachMessage) => eachMessage.id !== message.data.id),
        (draftMessages) => {
          draftMessages.push(message.data);
        }
      );
    });
    helproomsShift$.next(state);
  },
  /**
   * Initial Moderation Msgs List
   * @param messages
   * @param helproom_id
   */
  setInititalMessagesForReview: (messages: IMessage[], helproom_id: string): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      draftState.messages_for_action[helproom_id] = messages;
    });
    helproomsShift$.next(state);
  },
  /**
   * Update Live messages.
   * @param message
   * @param helproom_id
   */
  updateHelproomMessages: (message: EventFormat<IMessage>, helproom_id: string): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      if (!draftState.messages[helproom_id]) {
        draftState.messages[helproom_id] = [];
      }
      draftState.messages[helproom_id].push(
        produce(message.data, (draftMessage) => {
          draftMessage.is_read = draftState.active_component?.component.id === helproom_id && draftState.active_component?.type === ActiveHelproomTaskComponentType.helproomActiveSession;
        })
      );

      draftState.helprooms.forEach((eachHelproom) => {
        if (eachHelproom.helproom_id === helproom_id) {
          eachHelproom.last_active_message = message.data.message;
        }
      });
    });
    helproomsShift$.next(state);
  },
  /**
   * Set initial Helproom messages from http call.
   * @param messages
   * @param helproom_id
   */
  setIntitialHelproomMessages: (messages: IMessage[], helproom_id: string): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      draftState.messages[helproom_id] = produce(messages, () => messages);
    });
    helproomsShift$.next(state);
  },
  /**
   * Set initial Helproom messages from http call.
   * @param messages
   * @param helproom_id
   */
  setSessionEnded: (helproom_id: string): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      draftState.helprooms.forEach((eachHelproom) => {
        if (eachHelproom.id === helproom_id) {
          eachHelproom.door_status = false;
          eachHelproom.end_session = true;
        }
      });
      draftState.helprooms_staffs_chats.forEach((eachHelproom) => {
        if (eachHelproom.id === helproom_id) {
          eachHelproom.helproom.door_status = false;
        }
      });
      draftState.visitors_one_on_one.forEach((eachHelproom) => {
        if (eachHelproom.id === helproom_id) {
          eachHelproom.door_status = false;
        }
      });

      if (draftState.active_component?.component) {
        draftState.active_component.component.door_status = false;
        draftState.active_component.component.end_session = true;
      }
    });
    helproomsShift$.next(state);
  },
  /**
   **Update Real time Staff Messages
   * @param message
   * @param helproom_id
   */
  updateStaffersConversation: (message: EventFormat<IMessage>, helproom_id: string): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      if (!draftState.messages_staffs[helproom_id]) {
        draftState.messages_staffs[helproom_id] = [];
      }
      draftState.messages_staffs[helproom_id].push(
        produce(message.data, (draftMessage) => {
          draftMessage.is_read = draftState.active_component?.component.id === helproom_id && draftState.active_component?.type === ActiveHelproomTaskComponentType.staffersGroup;
        })
      );
      draftState.helprooms_staffs_chats.forEach((eachStaffChat) => {
        if (eachStaffChat.helproom_id === helproom_id) {
          eachStaffChat.last_active_message = message.data.message;
        }
      });
    });
    helproomsShift$.next(state);
  },
  /**
   **Initial Staff Conversation via HTTP
   * @param messages
   * @param helproom_id
   */
  setInitialStaffersConversation: (messages: IMessage[], helproom_id: string): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      draftState.messages_staffs[helproom_id] = produce(messages, () => messages);
    });
    helproomsShift$.next(state);
  },
  /**
   **Initial Staff Conversation via HTTP
   * @param messages
   * @param helproom_id
   */
  setInitialOneOnOneMessages: (messages: IMessage[], visitor_id: string): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      draftState.messages_one_on_one[visitor_id] = produce(messages, () => messages);
    });
    helproomsShift$.next(state);
  },
  /**
   ** Used to Toggle Component Pop Out or back Pop In
   * @param helproom_id
   * @param component_type
   * @param state
   */
  togglePopInOrOut: (
    helproom_id: string,
    component_type: ActiveHelproomTaskComponentType.helproomActiveSession | ActiveHelproomTaskComponentType.staffersGroup | ActiveHelproomTaskComponentType.visitorOneOnOne,
    popped_in_state: boolean
  ): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      if (component_type === ActiveHelproomTaskComponentType.helproomActiveSession) {
        draftState.helprooms.forEach((eachHelproom) => {
          if (eachHelproom.id === helproom_id) {
            eachHelproom.popped_out = popped_in_state;
          }
        });
      } else if (component_type === ActiveHelproomTaskComponentType.staffersGroup) {
        draftState.helprooms_staffs_chats.forEach((eachHelproomStaffChat) => {
          if (eachHelproomStaffChat.id === helproom_id) {
            eachHelproomStaffChat.popped_out = popped_in_state;
          }
        });
      } else if (component_type === ActiveHelproomTaskComponentType.visitorOneOnOne) {
        draftState.visitors_one_on_one.forEach((eachOneOnOne) => {
          if (eachOneOnOne.helproom_id === helproom_id) {
            eachOneOnOne.popped_out = popped_in_state;
          }
        });
      }
      draftState.active_component.component.popped_out = popped_in_state;
    });
    helproomsShift$.next(state);
  },
  /**
   ** Update Visitors via WebSocket
   * @param participant
   * @param helproom_id
   */
  updateParticipantsInHelproom: (participant: EventFormat<IParticipant>, helproom_id: string): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      draftState.helprooms.forEach((eachHelproom) => {
        if (eachHelproom.id === helproom_id) {
          if (participant.event === EventTypes.VISITOR_JOIN || participant.event === EventTypes.MODERATOR_JOIN) {
            eachHelproom.visitors[participant.data.id] = produce(participant.data, (draftParticipant) => {
              if (participant.event === EventTypes.MODERATOR_JOIN) {
                draftParticipant.is_moderator = true;
              }
            });
            if (participant.event === EventTypes.VISITOR_JOIN) {
              SoundOptions.setType('helproom_visitor_join');
            }
          } else if (participant.event === EventTypes.VISITOR_LEAVE || participant.event === EventTypes.MODERATOR_LEAVE) {
            const leftVisitor = eachHelproom.visitors[participant.data.id];
            leftVisitor.subscription?.unsubscribe();
            delete eachHelproom.visitors[participant.data.id];
          }
        }
      });
      draftState.visitors_one_on_one = draftState.visitors_one_on_one.filter((eachOneOnOne) => eachOneOnOne.id !== participant.data.id);
    });
    helproomsShift$.next(state);
  },
  /**
   * !Whenever Operator starts One on One With Visitor -
   * @param data
   * @param started
   */
  oneOnOneConversationInitiated: (data: IStafferVisitorOneOnOne, started = true): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      draftState.helprooms.forEach((eachHelproom) => {
        if (eachHelproom.id === data.helproom_id) {
          if (!eachHelproom.visitors[data.visitor_id].one_on_one_initiated && eachHelproom.visitors[data.visitor_id].subscription === undefined) {
            eachHelproom.visitors[data.visitor_id].subscription = draftState.client.subscribe(
              `${HelproomMessagingTopics.getVirtualTopicOneonOne(data.helproom_id, data.operator_id, data.visitor_id)}`,
              (message) => {
                HelproomsShiftStore.updateOneOnOneMessages(JSON.parse(message.body), data.visitor_id);
              }
            );
          }
          eachHelproom.visitors[data.visitor_id].one_on_one_initiated = started;
          eachHelproom.visitors[data.visitor_id].status = data.status;
        }
      });
      if (!draftState.visitors_one_on_one) {
        draftState.visitors_one_on_one = [];
      }
      draftState.visitors_one_on_one.push(
        produce(data, (draftData) => {
          draftData.id = data.visitor_id;
          draftData.display_name = `${data.visitor}-${data.operator}`;
        })
      );
    });
    helproomsShift$.next(state);
  },
  /**
   * Update One on One Messages
   * @param message
   * @param visitor_id
   */
  updateOneOnOneMessages: (message: EventFormat<IMessage>, visitor_id: string): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      if (!draftState.messages_one_on_one[visitor_id]) {
        draftState.messages_one_on_one[visitor_id] = [];
      }
      draftState.messages_one_on_one[visitor_id].push(
        produce(message.data, (draftMessage) => {
          draftMessage.is_read = draftState.active_component?.component.id === visitor_id;
        })
      );
      draftState.visitors_one_on_one.forEach((eachOneOnOne) => {
        if (eachOneOnOne.id === visitor_id) {
          eachOneOnOne.last_active_message = message.data.message;
        }
      });
    });
    helproomsShift$.next(state);
  },
  /**
   *! Mark one on one as complete
   * @param visitor_id
   * @param helproom_id
   */
  oneOnOneConversationCompleted: (visitor_id: string, helproom_id: string): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      draftState.helprooms.forEach((eachHelproom) => {
        if (eachHelproom.id === helproom_id) {
          eachHelproom.visitors[visitor_id].one_on_one_initiated = false;
          eachHelproom.visitors[visitor_id].subscription?.unsubscribe();
          eachHelproom.visitors[visitor_id].subscription = undefined;
        }
      });
      draftState.visitors_one_on_one = draftState.visitors_one_on_one.filter((eachOneOnOne) => eachOneOnOne.id !== visitor_id);
    });
    helproomsShift$.next(state);
  },
  /**
   *! Kick Out Visitor.
   * @param visitor_id
   * @param helproom_id
   */
  removeVisitor: (visitor_id: string, helproom_id: string): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      draftState.visitors_one_on_one.forEach((eachOneonOne) => {
        if (eachOneonOne.visitor_id === visitor_id) {
          eachOneonOne.subscriptions$?.unsubscribe();
          delete eachOneonOne.subscriptions$;
        }
      });
      draftState.helprooms.forEach((eachHelproom) => {
        if (eachHelproom.id === helproom_id) {
          delete eachHelproom.visitors[visitor_id];
        }
      });
    });
    helproomsShift$.next(state);
  },

  setActiveMessageInHelproom: (helproom_id: string, message: string): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      let messageRooms: IStaffersConversationGroup[] | IHelproomWithEverything[] | IStafferVisitorOneOnOne[] = draftState.helprooms;
      if (draftState.active_component.type === ActiveHelproomTaskComponentType.staffersGroup) {
        messageRooms = draftState.helprooms_staffs_chats;
      } else if (draftState.active_component.type === ActiveHelproomTaskComponentType.visitorOneOnOne) {
        messageRooms = draftState.visitors_one_on_one;
      }
      const messageRoom = messageRooms.find(
        (room: IStaffersConversationGroup | IHelproomWithEverything | IStafferVisitorOneOnOne) => room.helproom_id === helproom_id && room.id === draftState.active_component.component.id
      );
      if (messageRoom) {
        messageRoom.current_message = message;
      }
    });
    helproomsShift$.next(state);
  },
  clearMessage: (helproom_id: string): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      let messageRooms: IStaffersConversationGroup[] | IHelproomWithEverything[] | IStafferVisitorOneOnOne[] = draftState.helprooms;
      if (draftState.active_component.type === ActiveHelproomTaskComponentType.staffersGroup) {
        messageRooms = draftState.helprooms_staffs_chats;
      } else if (draftState.active_component.type === ActiveHelproomTaskComponentType.visitorOneOnOne) {
        messageRooms = draftState.visitors_one_on_one;
      }
      const messageRoom = messageRooms.find(
        (room: IStaffersConversationGroup | IHelproomWithEverything | IStafferVisitorOneOnOne) => room.helproom_id === helproom_id && room.id === draftState.active_component.component.id
      );
      if (messageRoom) {
        messageRoom.current_message = '';
      }
    });

    helproomsShift$.next(state);
  },
  /**
   *! Update Notepad Area.
   * @param helproom_id
   * @param note
   */
  updateNotePadArea: (helproom_id: string, note: string): void => {
    const state = produce(helproomsShift$.value, (draftState) => {
      let messageRooms: IStaffersConversationGroup[] | IHelproomWithEverything[] | IStafferVisitorOneOnOne[] = draftState.helprooms;
      if (draftState.active_component.type === ActiveHelproomTaskComponentType.staffersGroup) {
        messageRooms = draftState.helprooms_staffs_chats;
      } else if (draftState.active_component.type === ActiveHelproomTaskComponentType.visitorOneOnOne) {
        messageRooms = draftState.visitors_one_on_one;
      }
      const messageRoom = messageRooms.find(
        (room: IStaffersConversationGroup | IHelproomWithEverything | IStafferVisitorOneOnOne) => room.helproom_id === helproom_id && room.id === draftState.active_component.component.id
      );
      if (messageRoom) {
        messageRoom.notepad_content = note;
      }
    });
    helproomsShift$.next(state);
  },
  /**
   * Test For Blue Green
   */
  updateUUID: (id: string) => {
    const state = produce(helproomsShift$.value, (draftState) => {
      draftState.pongUUID = id;
    });
    helproomsShift$.next(state);
  },
};

/**
 ** Hook Usage everywhere.
 */

export default function useHelproomShift(): IHelproomShift {
  return useObservable<IHelproomShift>(helproomsShift$, helproomsShift$.value);
}

const defineHelproomAbilitiesForOperator = (access: IHelpoomAccess) => {
  const existingRules = AbilityStore.getAbilities().rules;

  const { can: allow, rules } = new AbilityBuilder(AppAbilities);
  if (access.approve_message) allow(HelproomShiftPermissions.APPROVE_MESSAGE, ShiftNames.HELPROOM_SHIFT);
  if (access.clear_messages) allow(HelproomShiftPermissions.CLEAR_MESSAGES, ShiftNames.HELPROOM_SHIFT);
  if (access.close_helproom_door) allow(HelproomShiftPermissions.CLOSE_HELPROOM_DOOR, ShiftNames.HELPROOM_SHIFT);
  if (access.edit_message) allow(HelproomShiftPermissions.EDIT_MESSAGE, ShiftNames.HELPROOM_SHIFT);
  if (access.chat) allow(HelproomShiftPermissions.CHAT, ShiftNames.HELPROOM_SHIFT);
  if (access.kick_out) allow(HelproomShiftPermissions.KICK_OUT, ShiftNames.HELPROOM_SHIFT);
  if (access.one_on_one_invitation) allow(HelproomShiftPermissions.ONE_ON_ONE_INVITATION, ShiftNames.HELPROOM_SHIFT);
  if (access.reject_message) allow(HelproomShiftPermissions.REJECT_MESSAGE, ShiftNames.HELPROOM_SHIFT);
  if (access.session_update) allow(HelproomShiftPermissions.SESSION_UPDATE, ShiftNames.HELPROOM_SHIFT);

  return [...existingRules.filter((iRule) => iRule.subject !== ShiftNames.HELPROOM_SHIFT), ...rules];
};

const revokeRulesForOperator = () => {
  const existingRules = AbilityStore.getAbilities().rules;
  return existingRules.filter((iRule) => iRule.subject !== ShiftNames.HELPROOM_SHIFT);
};
